import {
  TextInput,
  Textarea,
  Stack,
  Switch,
  Group,
  Button,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { CollectionMethod } from "../../../services/admin/collectionMethods.service";
import { required, scrollToFirstError } from "../../../utils/formValidation";

export interface CollectionMethodFormValues {
  name: string;
  description: string;
  isActive: boolean;
}

interface CollectionMethodFormProps {
  initialValues?: CollectionMethod;
  onSubmit: (values: CollectionMethodFormValues) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
}

export default function CollectionMethodForm({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitLabel,
}: CollectionMethodFormProps) {
  const form = useForm<CollectionMethodFormValues>({
    initialValues: {
      name: initialValues?.name || "",
      description: initialValues?.description || "",
      isActive: initialValues?.isActive ?? true,
    },
    validate: {
      name: required("Collection method name is required"),
    },
  });

  const handleSubmit = form.onSubmit(
    (values) => onSubmit(values),
    () => scrollToFirstError(form)
  );

  return (
    <form noValidate onSubmit={handleSubmit}>
      <Stack gap="md">
        <TextInput
          label="Collection Method"
          placeholder="e.g., Biopsy, Excision, Shave, Fine Needle Aspiration"
          withAsterisk
          {...form.getInputProps("name")}
        />

        <Textarea
          label="Description"
          placeholder="Enter method description"
          minRows={3}
          {...form.getInputProps("description")}
        />

        <Switch
          label="Active"
          description="Inactive methods will not appear in dropdowns"
          {...form.getInputProps("isActive", { type: "checkbox" })}
        />

        {(onCancel || submitLabel !== undefined) && (
          <Group justify="flex-end" gap="xs" mt="md">
            {onCancel && (
              <Button variant="default" onClick={onCancel} type="button">
                Cancel
              </Button>
            )}
            <Button type="submit" loading={isSubmitting}>
              {submitLabel || (initialValues ? "Update" : "Create")}
            </Button>
          </Group>
        )}
      </Stack>
    </form>
  );
}

export { useForm } from "@mantine/form";
